import React, { createContext, useContext, useState, useEffect } from 'react'
import { foldersAPI } from '../services/api'
import { useLocalMode } from './LocalModeContext'
import { useAuth } from './AuthContext'
import toast from 'react-hot-toast'
import frontendSync from '../services/frontendSync.js'
import { SYNC_CONFIG } from '../services/syncConfig.js'

/**
 * Folders Context for managing folders state and operations.
 * 
 * Provides folder data, CRUD operations, and note movement functionality
 * across the entire application.
 */

const FoldersContext = createContext({})

export const useFolders = () => {
  const context = useContext(FoldersContext)
  if (!context) {
    throw new Error('useFolders must be used within a FoldersProvider')
  }
  return context
}

export const FoldersProvider = ({ children }) => {
  const [folders, setFolders] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentFolder, setCurrentFolder] = useState(null)
  const [isShowingAllNotes, setIsShowingAllNotes] = useState(true)
  const { isLocalMode, createLocalFolder, localFolders, loading: localLoading, deleteLocalFolder, moveNoteToFolderLocal } = useLocalMode()
  const { user } = useAuth()
  const [defaultFolders, setDefaultFolders] = useState({
    pinned: null,
    sensitive: null
  })

  // Sync folders state with the appropriate mode
  useEffect(() => {
    if (isLocalMode) {
      // In local mode, use localFolders from LocalModeContext
      console.log('🔄 FoldersContext: Switching to local mode, syncing folders')
      const orderedLocalFolders = orderFolders(localFolders || [])
      setFolders(orderedLocalFolders)
      
      // Update default folders for local mode
      const pinnedFolder = orderedLocalFolders.find(f => f.folderType === 'PINNED')
      const sensitiveFolder = orderedLocalFolders.find(f => f.folderType === 'SENSITIVE')
      setDefaultFolders({
        pinned: pinnedFolder || null,
        sensitive: sensitiveFolder || null
      })
    } else {
      // In cloud mode, clear folders initially - they'll be fetched by fetchFolders
      console.log('🔄 FoldersContext: Switching to cloud mode, clearing local folders')
      setFolders([])
      setDefaultFolders({
        pinned: null,
        sensitive: null
      })
    }
  }, [isLocalMode, localFolders])

  /**
   * Fetch all folders for the current user
   */
  const fetchFolders = async () => {
    try {
      setLoading(true)
      console.log('🔍 FoldersContext: Fetching folders')
      
      let foldersData
      
      if (isLocalMode) {
        // Get folders from local storage - NO NETWORK ACTIVITY
        console.log('🔍 FoldersContext: Fetching folders from local storage (offline)')
        foldersData = localFolders || []
        console.log('✅ FoldersContext: Local folders received:', foldersData)
        
        // In local mode, LocalModeContext handles default folder creation
        // We just use whatever folders are available from localFolders state
        
        // Expose folders globally for other components
        window.foldersContextData = { folders: foldersData }
        console.log('🔍 FoldersContext: Exposed', foldersData.length, 'local folders to global context')
        
        // Dispatch foldersLoaded event for other components (like NotesContext)
        window.dispatchEvent(new CustomEvent('foldersLoaded', { 
          detail: { folders: foldersData } 
        }))
      } else {
        if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
          console.log('🔍 FoldersContext: Fetching folders via frontend sync (Drive)')
          
          // 🚀 OPTIMIZATION: Show cached folders immediately for instant UI
          try {
            console.log('💾 FoldersContext: Loading cached folders first for instant display...')
            const cachedFolders = await frontendSync.getAllFoldersFromCacheDB()
            if (cachedFolders.length > 0) {
              console.log('⚡ FoldersContext: Showing', cachedFolders.length, 'cached folders immediately')
              
              // Apply correct ordering to cached folders
              const orderedCachedFolders = orderFolders(cachedFolders)
              setFolders(orderedCachedFolders)
              
              // Expose cached folders globally immediately
              window.foldersContextData = { folders: orderedCachedFolders }
              console.log('🔍 FoldersContext: Exposed', orderedCachedFolders.length, 'cached folders to global context (immediate)')
              
              // Dispatch foldersLoaded event for immediate note filtering
              console.log('🔄 FoldersContext: Dispatching foldersLoaded event for immediate note filtering')
              window.dispatchEvent(new CustomEvent('foldersLoaded', { 
                detail: { folders: orderedCachedFolders } 
              }))
            }
          } catch (error) {
            console.warn('⚠️ FoldersContext: Cache load failed, will fetch fresh:', error.message)
          }
          
          // Now sync with Google Drive in background (non-blocking)
          console.log('🔄 FoldersContext: Starting background sync with Google Drive...')
          foldersData = await frontendSync.listFoldersFrontend()
          
          // Only update if we got different data from the API
          if (foldersData && foldersData.length > 0) {
            console.log('🔄 FoldersContext: Background sync completed, updating with', foldersData.length, 'folders')
            
            // Apply correct ordering to fresh folders
            const orderedFreshFolders = orderFolders(foldersData)
            
            // Check if the data actually changed to avoid unnecessary updates
            const currentFolders = window.foldersContextData?.folders || []
            const hasChanges = JSON.stringify(orderedFreshFolders) !== JSON.stringify(currentFolders)
            
            if (hasChanges) {
              console.log('🔄 FoldersContext: Fresh data differs from cache, updating folders')
              setFolders(orderedFreshFolders)
              
              // Update global context
              window.foldersContextData = { folders: orderedFreshFolders }
              console.log('🔍 FoldersContext: Exposed', orderedFreshFolders.length, 'fresh folders to global context (background sync)')
              
              // Dispatch foldersUpdated event for any components that need fresh data
              window.dispatchEvent(new CustomEvent('foldersUpdated', { 
                detail: { folders: orderedFreshFolders } 
              }))
            } else {
              console.log('✅ FoldersContext: Background sync completed, no changes detected')
            }
          } else {
            console.log('⚠️ FoldersContext: Background sync returned no data, keeping cached folders')
            // Keep the cached data we already set
            foldersData = window.foldersContextData?.folders || []
          }
        } else {
          // Traditional backend API call
          console.log('🔍 FoldersContext: Fetching folders from backend API')
          foldersData = await foldersAPI.getFolders()
          
        console.log('✅ FoldersContext: API folders received:', foldersData)
      }
      }
      
      // Apply consistent folder ordering to all modes
      foldersData = orderFolders(foldersData)
      
      // Set folders in state for all modes
      setFolders(foldersData)
      
      // Set default folders for reference (used by other components)
      const pinnedFolder = foldersData.find(f => f.folderType === 'PINNED')
      const sensitiveFolder = foldersData.find(f => f.folderType === 'SENSITIVE')
      
      setDefaultFolders({
        pinned: pinnedFolder || null,
        sensitive: sensitiveFolder || null
      })
      
      // If no default folders exist, initialize them automatically (cloud mode only)
      if (!isLocalMode && (!pinnedFolder || !sensitiveFolder)) {
        console.log('🔍 FoldersContext: Missing default folders in cloud mode, initializing...')
        try {
          console.log('🔍 FoldersContext: Using cloud mode initialization')
          await initializeDefaultFolders()
          // Re-fetch folders after cloud creation
          return await fetchFolders()
        } catch (error) {
          console.error('❌ FoldersContext: Failed to auto-initialize default folders:', error)
        }
      }
      
      return foldersData
    } catch (error) {
      console.error('❌ FoldersContext: Error fetching folders:', error)
      toast.error('Failed to load folders')
      return []
    } finally {
      setLoading(false)
    }
  }

/**
 * Order folders to ensure correct tab bar sequence:
 * [All Notes (handled by UI)][Pinned][Sensitive][Custom folders by creation date]
 */
const orderFolders = (folders) => {
  if (!folders || !Array.isArray(folders)) return []
  
  const ordered = []
  
  // 1. Add PINNED folder first
  const pinnedFolder = folders.find(f => f.folderType === 'PINNED')
  if (pinnedFolder) {
    ordered.push(pinnedFolder)
  }
  
  // 2. Add SENSITIVE folder second  
  const sensitiveFolder = folders.find(f => f.folderType === 'SENSITIVE')
  if (sensitiveFolder) {
    ordered.push(sensitiveFolder)
  }
  
  // 3. Add CUSTOM folders sorted by creation date (oldest first)
  const customFolders = folders
    .filter(f => f.folderType === 'CUSTOM')
    .sort((a, b) => {
      const dateA = new Date(a.createdAt || 0)
      const dateB = new Date(b.createdAt || 0)
      return dateA - dateB // Oldest first
    })
  
  ordered.push(...customFolders)
  
  console.log('📋 FoldersContext: Ordered folders:', ordered.map(f => `${f.name}(${f.folderType})`).join(' → '))
  return ordered
  }

  /**
   * Initialize default folders for the user
   */
  const initializeDefaultFolders = async () => {
    try {
      setLoading(true)
      console.log('🔍 FoldersContext: Initializing default folders')
      
      // SAFETY: Never run this in local mode - it's cloud-only
      if (isLocalMode) {
        console.log('⚠️ FoldersContext: initializeDefaultFolders called in local mode - aborting (should use LocalModeContext instead)')
        return { 
          created: false, 
          message: 'Local mode should not use cloud initialization' 
        }
      }
      
      let result
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        // First, check if default folders already exist in Google Drive
        console.log('🔍 FoldersContext: Checking for existing default folders in Google Drive...')
        const existingFolders = await frontendSync.listFoldersFrontend()
        const existingPinned = existingFolders.find(f => f.folderType === 'PINNED')
        const existingSensitive = existingFolders.find(f => f.folderType === 'SENSITIVE')
        
        console.log('🔍 FoldersContext: Found existing folders:', {
          pinned: !!existingPinned,
          sensitive: !!existingSensitive
        })
        
        // Only create folders that don't already exist
        const foldersToCreate = []
        if (!existingPinned) {
          foldersToCreate.push({ name: 'Pinned', folderType: 'PINNED' })
        }
        if (!existingSensitive) {
          foldersToCreate.push({ name: 'Sensitive', folderType: 'SENSITIVE' })
        }
        
        if (foldersToCreate.length === 0) {
          console.log('✅ FoldersContext: All default folders already exist, skipping creation')
          result = { 
            created: false, 
            pinned: existingPinned, 
            sensitive: existingSensitive,
            message: 'Default folders already exist'
          }
        } else {
          console.log(`🔄 FoldersContext: Creating ${foldersToCreate.length} missing default folders...`)
          
          // Create only the missing folders
          const createdFolders = {}
          for (const folderData of foldersToCreate) {
            console.log(`📁 FoldersContext: Creating ${folderData.folderType} folder...`)
            const created = await frontendSync.createFolderFrontend(folderData)
            createdFolders[folderData.folderType.toLowerCase()] = created
          }
          
          result = { 
            created: true, 
            pinned: existingPinned || createdFolders.pinned,
            sensitive: existingSensitive || createdFolders.sensitive
          }
          
          console.log('✅ FoldersContext: Successfully created missing default folders')
        }
      } else {
        result = await foldersAPI.initializeDefaultFolders()
      }
      console.log('✅ FoldersContext: Default folders initialized:', result)
      
      if (result.created) {
        toast.success('Default folders created successfully')
        // Refresh folders list
        await fetchFolders()
      }
      
      return result
    } catch (error) {
      console.error('❌ FoldersContext: Error initializing default folders:', error)
      toast.error('Failed to initialize default folders')
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Create a new custom folder
   */
  const createCustomFolder = async (folderData) => {
    try {
      setLoading(true)
      console.log('🔍 FoldersContext: Creating folder:', folderData)
      
      let newFolder
      
      if (isLocalMode) {
        // Create folder in local storage
        console.log('🔍 FoldersContext: Creating folder in local mode')
        newFolder = await createLocalFolder(folderData)
        console.log('✅ FoldersContext: Local folder created:', newFolder)
        
        // Add to local state
        setFolders(prev => [...prev, newFolder])
      } else {
        if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
          console.log('🔍 FoldersContext: Creating folder via frontend sync (optimistic)')
          // Optimistic local state update
          const optimistic = {
            folderId: folderData.folderId || `folder-${Date.now()}-${Math.random().toString(36).substr(2,9)}`,
            name: folderData.name,
            folderType: folderData.folderType || 'CUSTOM',
            color: folderData.color || null,
            createdAt: new Date().toISOString(),
            lastModified: new Date().toISOString(),
            isOptimistic: true
          }
          setFolders(prev => [...prev, optimistic])
          try {
            const created = await frontendSync.createFolderFrontend(optimistic)
            // Reconcile optimistic
            setFolders(prev => prev.map(f => f.folderId === optimistic.folderId ? { ...created, isOptimistic: false } : f))
            newFolder = created
          } catch (e) {
            // Rollback optimistic on failure
            setFolders(prev => prev.filter(f => f.folderId !== optimistic.folderId))
            throw e
          }
        } else {
          // Create folder via API
          console.log('🔍 FoldersContext: Creating folder via API')
          newFolder = await foldersAPI.createFolder(folderData)
        }
        console.log('✅ FoldersContext: API folder created:', newFolder)
        
        // Add to local state
        if (!SYNC_CONFIG.USE_FRONTEND_SYNC) {
          setFolders(prev => [...prev, newFolder])
        }
      }
      
      toast.success(`Folder "${newFolder.name}" created successfully`)
      return newFolder
    } catch (error) {
      console.error('❌ FoldersContext: Error creating folder:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create folder'
      toast.error(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Update an existing custom folder
   */
  const updateCustomFolder = async (folderId, updateData) => {
    try {
      setLoading(true)
      console.log('🔍 FoldersContext: Updating folder:', folderId, updateData)
      
      if (isLocalMode) {
        // Local mode handled elsewhere
      }
      let updatedFolder
      if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        // Optimistic update
        setFolders(prev => prev.map(f => f.folderId === folderId ? { ...f, ...updateData } : f))
        try {
          updatedFolder = await frontendSync.updateFolderFrontend(folderId, updateData)
          setFolders(prev => prev.map(f => f.folderId === folderId ? updatedFolder : f))
        } catch (e) {
          // On failure, refetch list from frontend to reconcile
          const fresh = await frontendSync.listFoldersFrontend()
          setFolders(fresh)
          throw e
        }
      } else {
        updatedFolder = await foldersAPI.updateFolder(folderId, updateData)
        setFolders(prev => prev.map(f => f.folderId === folderId ? updatedFolder : f))
      }
      console.log('✅ FoldersContext: Folder updated:', updatedFolder)
      
      toast.success(`Folder "${updatedFolder.name}" updated successfully`)
      return updatedFolder
    } catch (error) {
      console.error('❌ FoldersContext: Error updating folder:', error)
      const errorMessage = error.response?.data?.message || 'Failed to update folder'
      toast.error(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Delete a custom folder
   */
  const deleteCustomFolder = async (folderId) => {
    try {
      setLoading(true)
      console.log('🔍 FoldersContext: Deleting folder:', folderId)
      
      if (isLocalMode) {
        // Delete folder from local storage
        console.log('🔍 FoldersContext: Deleting folder from local storage')
        await deleteLocalFolder(folderId)
        console.log('✅ FoldersContext: Local folder deleted:', folderId)
      } else {
        // Optimistic removal
        const previous = folders
        setFolders(prev => prev.filter(f => f.folderId !== folderId))
        try {
          if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
            console.log('🔍 FoldersContext: Deleting folder via frontend sync')
            await frontendSync.deleteFolderFrontend(folderId)
          } else {
            console.log('🔍 FoldersContext: Deleting folder via API')
            await foldersAPI.deleteFolder(folderId)
          }
        } catch (e) {
          // Rollback UI
          setFolders(previous)
          throw e
        }
        console.log('✅ FoldersContext: API folder deleted:', folderId)
      }
      
      // Remove from local state
      const deletedFolder = folders.find(f => f.folderId === folderId)
      
      // If we were viewing the deleted folder, switch to "All Notes"
      if (currentFolder?.folderId === folderId) {
        showAllNotes()
      }
      
      toast.success(`Folder "${deletedFolder?.name}" deleted successfully`)
      return true
    } catch (error) {
      console.error('❌ FoldersContext: Error deleting folder:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete folder'
      toast.error(errorMessage)
      throw error
    } finally {
      setLoading(false)
    }
  }

  /**
   * Move a note to a different folder
   */
  const moveNoteToFolder = async (noteId, folderId, password = null) => {
    try {
      console.log('🔍 FoldersContext: Moving note to folder:', noteId, folderId)
      
      // Optimistic UI update: notify listeners via custom event so notes lists can update instantly
      try {
        window.dispatchEvent(new CustomEvent('noteFolderChanged', { detail: { noteId, folderId: folderId || null } }))
      } catch (_) {}

      if (isLocalMode) {
        // Use local storage operations for local mode
        await moveNoteToFolderLocal(noteId, folderId, password)
      } else if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        // Frontend-only move for all operations (including sensitive with password)
        await frontendSync.moveNoteToFolderFrontend(noteId, folderId, password)
      } else {
        // Use backend API for cloud mode
        const moveData = folderId ? { folderId } : {}
        
        // Add password if provided (for sensitive folder operations)
        if (password) {
          moveData.password = password
        }
        
        await foldersAPI.moveNote(noteId, moveData)
      }
      
      const targetFolder = folders.find(f => f.folderId === folderId)
      const targetName = targetFolder ? targetFolder.name : 'root'
      
      console.log('✅ FoldersContext: Note moved to:', targetName)
      toast.success(`Note moved to ${targetName}`)
      
      // Refresh folder data to update note counts (non-blocking)
      fetchFolders().catch(() => {})
      
      return true
    } catch (error) {
      console.error('❌ FoldersContext: Error moving note:', error)
      
      // Handle password-related errors specifically
      if (error.response?.status === 400 && error.response?.data?.includes?.('Password required')) {
        toast.error('Password required for sensitive folder operations')
      } else if (error.response?.status === 400 && error.response?.data?.includes?.('Invalid password')) {
        toast.error('Invalid password - note move failed')
      } else {
        toast.error('Failed to move note')
      }
      throw error
    }
  }

  /**
   * Move a note to root (no folder)
   */
  const moveNoteToRoot = async (noteId, password = null) => {
    try {
      console.log('🔍 FoldersContext: Moving note to root:', noteId)
      
      const moveData = {}
      
      // Add password if provided (for moving from sensitive folder)
      if (password) {
        moveData.password = password
        await foldersAPI.moveNote(noteId, moveData)
      } else if (SYNC_CONFIG.USE_FRONTEND_SYNC) {
        // Optimistic event
        try { window.dispatchEvent(new CustomEvent('noteFolderChanged', { detail: { noteId, folderId: null } })) } catch (_) {}
        await frontendSync.moveNoteToRootFrontend(noteId)
      } else {
        await foldersAPI.moveNoteToRoot(noteId)
      }
      
      console.log('✅ FoldersContext: Note moved to root')
      toast.success('Note moved to root')
      
      // Refresh folder data to update note counts (non-blocking)
      fetchFolders().catch(() => {})
      
      return true
    } catch (error) {
      console.error('❌ FoldersContext: Error moving note to root:', error)
      
      // Handle password-related errors specifically
      if (error.response?.status === 400 && error.response?.data?.includes?.('Password required')) {
        toast.error('Password required for sensitive folder operations')
      } else if (error.response?.status === 400 && error.response?.data?.includes?.('Invalid password')) {
        toast.error('Invalid password - note move failed')
      } else {
        toast.error('Failed to move note')
      }
      throw error
    }
  }


  /**
   * Quick action: Pin a note
   */
  const pinNote = async (noteId) => {
    try {
      console.log('🔍 FoldersContext: Pinning note:', noteId)
      
      // Ensure default folders exist before pinning
      if (!hasDefaultFolders()) {
        console.log('🔍 FoldersContext: Default folders missing, initializing...')
        const result = await initializeDefaultFolders()
        if (!result.created && !hasDefaultFolders()) {
          // If folders weren't created and still don't exist, fetch them
          await fetchFolders()
        }
      }
      
      await foldersAPI.pinNote(noteId)
      
      console.log('✅ FoldersContext: Note pinned')
      toast.success('Note pinned successfully')
      
      // Refresh folder data to update note counts
      await fetchFolders()
      
      return true
    } catch (error) {
      console.error('❌ FoldersContext: Error pinning note:', error)
      toast.error('Failed to pin note')
      throw error
    }
  }

  /**
   * Quick action: Mark note as sensitive
   */
  const markNoteSensitive = async (noteId, password) => {
    try {
      console.log('🔍 FoldersContext: Marking note as sensitive:', noteId)
      
      // Ensure default folders exist before marking as sensitive
      if (!hasDefaultFolders()) {
        console.log('🔍 FoldersContext: Default folders missing, initializing...')
        const result = await initializeDefaultFolders()
        if (!result.created && !hasDefaultFolders()) {
          // If folders weren't created and still don't exist, fetch them
          await fetchFolders()
        }
      }
      
      const moveData = { folderType: 'SENSITIVE', password }
      await foldersAPI.moveNote(noteId, moveData)
      
      console.log('✅ FoldersContext: Note marked as sensitive')
      toast.success('Note marked as sensitive')
      
      // Refresh folder data to update note counts
      await fetchFolders()
      
      return true
    } catch (error) {
      console.error('❌ FoldersContext: Error marking note as sensitive:', error)
      
      // Handle password-related errors specifically
      if (error.response?.status === 400 && error.response?.data?.includes?.('Password required')) {
        toast.error('Password required for sensitive folder operations')
      } else if (error.response?.status === 400 && error.response?.data?.includes?.('Invalid password')) {
        toast.error('Invalid password - note move failed')
      } else {
        toast.error('Failed to mark note as sensitive')
      }
      throw error
    }
  }

  /**
   * Set the currently active folder
   */
  const selectFolder = (folder) => {
    console.log('🔍 FoldersContext: Selecting folder:', folder?.name || 'null')
    setCurrentFolder(folder)
    setIsShowingAllNotes(false)
  }

  /**
   * Show all notes (root notes without folders)
   */
  const showAllNotes = () => {
    console.log('🔍 FoldersContext: Showing all notes')
    setCurrentFolder(null)
    setIsShowingAllNotes(true)
  }

  /**
   * Cycle to next folder
   */
  const cycleToNextFolder = () => {
    // Create ordered folder list: "All Notes" + all folders
    const orderedFolders = [null, ...folders] // null represents "All Notes"
    
    if (orderedFolders.length <= 1) return

    const currentIndex = orderedFolders.findIndex(folder => 
      folder?.folderId === currentFolder?.folderId
    )
    
    const nextIndex = currentIndex >= orderedFolders.length - 1 ? 0 : currentIndex + 1
    const nextFolder = orderedFolders[nextIndex]
    
    if (nextFolder === null) {
      showAllNotes()
    } else {
      selectFolder(nextFolder)
    }
  }

  /**
   * Cycle to previous folder
   */
  const cycleToPreviousFolder = () => {
    // Create ordered folder list: "All Notes" + all folders
    const orderedFolders = [null, ...folders] // null represents "All Notes"
    
    if (orderedFolders.length <= 1) return

    const currentIndex = orderedFolders.findIndex(folder => 
      folder?.folderId === currentFolder?.folderId
    )
    
    const prevIndex = currentIndex <= 0 ? orderedFolders.length - 1 : currentIndex - 1
    const prevFolder = orderedFolders[prevIndex]
    
    if (prevFolder === null) {
      showAllNotes()
    } else {
      selectFolder(prevFolder)
    }
  }

  /**
   * Get folder by type (for default folders)
   */
  const getFolderByType = (folderType) => {
    return folders.find(folder => folder.folderType === folderType) || null
  }

  /**
   * Check if a note move operation requires password (involves sensitive folder)
   */
  const requiresPasswordForMove = (note, targetFolderId) => {
    const currentFolder = note.folderId ? folders.find(f => f.folderId === note.folderId) : null
    const targetFolder = targetFolderId ? folders.find(f => f.folderId === targetFolderId) : null
    
    const isCurrentSensitive = currentFolder?.folderType === 'SENSITIVE'
    const isTargetSensitive = targetFolder?.folderType === 'SENSITIVE'
    
    // Requires password if moving TO or FROM sensitive folder
    return isCurrentSensitive || isTargetSensitive
  }

  /**
   * Get operation type for password modal
   */
  const getPasswordOperationType = (note, targetFolderId) => {
    const currentFolder = note.folderId ? folders.find(f => f.folderId === note.folderId) : null
    const targetFolder = targetFolderId ? folders.find(f => f.folderId === targetFolderId) : null
    
    const isCurrentSensitive = currentFolder?.folderType === 'SENSITIVE'
    const isTargetSensitive = targetFolder?.folderType === 'SENSITIVE'
    
    if (!isCurrentSensitive && isTargetSensitive) {
      return 'encrypt'
    } else if (isCurrentSensitive && !isTargetSensitive) {
      return 'decrypt'
    } else {
      return 'move'
    }
  }

  /**
   * Get custom folders only
   */
  const getCustomFolders = () => {
    return folders.filter(folder => folder.folderType === 'CUSTOM')
  }

  /**
   * Check if current user has default folders
   */
  const hasDefaultFolders = () => {
    return defaultFolders.pinned && defaultFolders.sensitive
  }

  // Load folders when user is authenticated or when in local mode
  useEffect(() => {
    if (isLocalMode) {
      // In local mode, wait for LocalModeContext to finish loading before fetching folders
      if (!localLoading) {
        console.log('🔍 FoldersContext: Local mode ready, fetching folders from local storage')
        fetchFolders()
      } else {
        console.log('🔍 FoldersContext: Local mode still loading, waiting...')
      }
    } else if (user) {
      // In cloud mode, fetch folders when user is authenticated
      fetchFolders()
    } else {
      // Clear folders when user logs out (cloud mode only)
      setFolders([])
      setCurrentFolder(null)
      setDefaultFolders({
        pinned: null,
        sensitive: null
      })
    }
  }, [user, isLocalMode, localLoading, localFolders])

  const value = {
    // State
    folders,
    currentFolder,
    isShowingAllNotes,
    loading,
    defaultFolders,
    
    // Actions
    fetchFolders,
    initializeDefaultFolders,
    createCustomFolder,
    updateCustomFolder,
    deleteCustomFolder,
    moveNoteToFolder,
    moveNoteToRoot,
    pinNote,
    markNoteSensitive,
    selectFolder,
    showAllNotes,
    cycleToNextFolder,
    cycleToPreviousFolder,
    
    // Utilities
    getFolderByType,
    getCustomFolders,
    hasDefaultFolders,
    requiresPasswordForMove,
    getPasswordOperationType
  }

  return (
    <FoldersContext.Provider value={value}>
      {children}
    </FoldersContext.Provider>
  )
}

export default FoldersContext 